import { getApiClient } from '../api';
import type { Municipalities } from '../api';
import type { DatasetCardItem } from '../components/datasets/DatasetCard/DatasetCard';
import { logoUrl as buildLogoUrl } from '../utils/logoUrl';

/** BE detailed response shape (no schema in OpenAPI) */
interface DetailedVariant {
  body_code?: string;
  municipality_text_id?: string;
  municipality_id?: number;
  term_from?: number;
  term_to?: number;
  urlDataset?: string;
  urlHlasovaciData?: string;
  posledniAktualizace?: string | null;
  datasetPoznamka?: string | null;
  uplnyDataset?: boolean;
}

interface DetailedOrgan {
  varianty?: Record<string, DetailedVariant>;
}

interface DetailedResponse {
  logo?: string;
  organy?: Record<string, DetailedOrgan>;
}

const PARALLEL_LIMIT = 8;

function flattenDetailedToCards(
  municipalityId: number,
  municipalityTextId: string,
  typCode: string,
  detailed: DetailedResponse
): DatasetCardItem[] {
  const cards: DatasetCardItem[] = [];
  const logoUrl = detailed.logo ? buildLogoUrl(municipalityTextId) : null;

  if (!detailed.organy || typeof detailed.organy !== 'object') {
    return cards;
  }

  for (const [bodyType, organ] of Object.entries(detailed.organy)) {
    const varianty = organ?.varianty;
    if (!varianty || typeof varianty !== 'object') continue;

    const resolvedTyp = (typCode ?? '').trim();

  for (const [variantKey, variant] of Object.entries(varianty)) {
      cards.push({
        id: `${municipalityId}-${bodyType}-${variantKey}`,
        title: '',
        municipalityId: municipalityId,
        municipalityTextId: municipalityTextId,
        typCode: resolvedTyp,
        bodyCode: variant.body_code ?? bodyType,
        termFrom: variant.term_from ?? null,
        termTo: variant.term_to ?? null,
        logoUrl,
        posledniAktualizace: variant.posledniAktualizace ?? null,
        datasetPoznamka: variant.datasetPoznamka ?? null,
        uplnyDataset: variant.uplnyDataset,
        urlDataset: variant.urlDataset ?? null,
        urlZdroj: variant.urlHlasovaciData ?? null,
      });
    }
  }

  return cards;
}

async function fetchDetailedInBatches(
  municipalities: Municipalities[]
): Promise<DatasetCardItem[]> {
  const client = getApiClient();
  const allCards: DatasetCardItem[] = [];

  for (let i = 0; i < municipalities.length; i += PARALLEL_LIMIT) {
    const batch = municipalities.slice(i, i + PARALLEL_LIMIT);
    const results = await Promise.allSettled(
      batch.map((m) =>
        client.getDetailedByMunicipalityId(m.text_id).then((data) => ({
          municipality: m,
          detailed: data as unknown as DetailedResponse,
        }))
      )
    );

    for (const result of results) {
      if (result.status === 'fulfilled' && result.value.detailed) {
        const { municipality, detailed } = result.value;
        allCards.push(
          ...flattenDetailedToCards(
            municipality.id,
            municipality.text_id,
            municipality.typ_code ?? '',
            detailed
          )
        );
      }
    }
  }

  return allCards;
}

export type DatasetsLoaderData = {
  datasets: DatasetCardItem[];
};

export async function DatasetsLoader(): Promise<DatasetsLoaderData> {
  const client = getApiClient();
  const municipalities = await client.getMunicipalities();
  const datasets = await fetchDetailedInBatches(municipalities);
  return { datasets };
}
